/**
 * Painel Operacional Admin — redesign moderno
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, CardContent, Button,
  TextField, InputAdornment, Avatar, Chip, CircularProgress,
  Alert, Pagination,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import PersonIcon        from '@mui/icons-material/Person';
import BusinessIcon      from '@mui/icons-material/Business';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupIcon         from '@mui/icons-material/Group';
import ReceiptIcon       from '@mui/icons-material/Receipt';
import operacionalService from '../../services/operacionalService';
import clinicaService     from '../../services/clinicaService';

/* ─── palette ─── */
const ORANGE = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE   = '#2563EB';
const GREEN  = '#10B981';
const RED    = '#EF4444';

const fmt = (v) =>
  `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ═══════════════════════════════════════
   Card de Clínica
═══════════════════════════════════════ */
const ClinicaCard = ({ clinica, onVerPerfil }) => {
  const saldo    = parseFloat(clinica.valorTotalFaturamento || 0) - parseFloat(clinica.valorTotalDespesas || 0);
  const initials = clinica.nome?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'C';

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: 'transform 0.18s, box-shadow 0.18s',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 8px 28px rgba(249,115,22,0.12)',
          borderColor: 'rgba(249,115,22,0.3)',
        },
      }}
    >
      {/* top accent */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_L} 100%)` }} />

      <CardContent sx={{ p: 2.5, flex: 1 }}>
        {/* header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Avatar
            sx={{
              width: 48, height: 48,
              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
              fontSize: 16, fontWeight: 800,
              boxShadow: '0 3px 10px rgba(249,115,22,0.3)',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A', lineHeight: 1.3 }}
              noWrap
            >
              {clinica.nome}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.2 }} noWrap>
              {clinica.cnpj || clinica.cpf || 'Sem CNPJ/CPF'}
            </Typography>
          </Box>
        </Box>

        {/* stats grid */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 2 }}>
          {/* usuários */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <GroupIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
              <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>Usuários</Typography>
            </Box>
            <Chip
              label={clinica.numeroUsuarios || 0}
              size="small"
              sx={{ bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, fontWeight: 700, fontSize: 11, height: 20 }}
            />
          </Box>

          {/* faturamento */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <TrendingUpIcon sx={{ fontSize: 15, color: GREEN }} />
              <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>Faturamento</Typography>
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: GREEN }}>
              {fmt(clinica.valorTotalFaturamento)}
            </Typography>
          </Box>

          {/* despesas */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <TrendingDownIcon sx={{ fontSize: 15, color: RED }} />
              <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>Despesas</Typography>
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: RED }}>
              {fmt(clinica.valorTotalDespesas)}
            </Typography>
          </Box>

          {/* saldo */}
          <Box
            sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              pt: 1.2, mt: 0.5,
              borderTop: '1px dashed #E2E8F0',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>Saldo</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: saldo >= 0 ? GREEN : RED }}>
              {fmt(saldo)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* action */}
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
          onClick={() => onVerPerfil(clinica)}
          sx={{
            borderRadius: 2,
            background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
            boxShadow: 'none',
            fontSize: 12.5,
            fontWeight: 600,
            py: 1,
            textTransform: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #EA580C 0%, ${ORANGE} 100%)`,
              boxShadow: '0 4px 12px rgba(249,115,22,0.35)',
            },
          }}
        >
          Ver Perfil da Clínica
        </Button>
      </Box>
    </Card>
  );
};

/* ═══════════════════════════════════════
   Card de Usuário
═══════════════════════════════════════ */
const UserCard = ({ usuario, onVerPerfil }) => {
  const saldo    = parseFloat(usuario.valorTotalFaturamento || 0) - parseFloat(usuario.valorTotalDespesas || 0);
  const initials = usuario.nome?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'U';

  const tipoMap = {
    PF:      { label: 'PF',      bg: 'rgba(249,115,22,0.1)', color: ORANGE },
    PJ:      { label: 'PJ',      bg: 'rgba(37,99,235,0.1)',  color: BLUE  },
    HIBRIDO: { label: 'Híbrido', bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
  };
  const tipo = tipoMap[usuario.tipoPessoa] || { label: usuario.tipoPessoa, bg: '#F1F5F9', color: '#64748B' };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: 'transform 0.18s, box-shadow 0.18s',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 8px 28px rgba(37,99,235,0.1)',
          borderColor: 'rgba(37,99,235,0.25)',
        },
      }}
    >
      {/* top accent — azul para usuário */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BLUE} 0%, #60A5FA 100%)` }} />

      <CardContent sx={{ p: 2.5, flex: 1 }}>
        {/* header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar
            sx={{
              width: 48, height: 48,
              background: `linear-gradient(135deg, ${BLUE} 0%, #60A5FA 100%)`,
              fontSize: 15, fontWeight: 800,
              boxShadow: '0 3px 10px rgba(37,99,235,0.25)',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A', lineHeight: 1.3 }} noWrap>
              {usuario.nome}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.2 }} noWrap>
              {usuario.email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, mt: 0.6, flexWrap: 'wrap' }}>
              <Chip
                label={tipo.label}
                size="small"
                sx={{ bgcolor: tipo.bg, color: tipo.color, fontWeight: 700, fontSize: 10, height: 18 }}
              />
              {!usuario.ativo && (
                <Chip
                  label="Inativo"
                  size="small"
                  sx={{ bgcolor: '#F1F5F9', color: '#94A3B8', fontWeight: 600, fontSize: 10, height: 18 }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* clínica */}
        {usuario.nomeClinica && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.8,
              bgcolor: '#F8FAFC', borderRadius: 1.5,
              px: 1.2, py: 0.7, mb: 1.8,
            }}
          >
            <BusinessIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
            <Typography sx={{ fontSize: 12, color: '#64748B' }} noWrap>{usuario.nomeClinica}</Typography>
          </Box>
        )}

        {/* stats */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.8 }}>
          {[
            { label: 'Faturam.', val: usuario.totalFaturamentos || 0, color: GREEN },
            { label: 'Despesas', val: usuario.totalDespesas     || 0, color: RED   },
            { label: 'Docs',     val: usuario.totalDocumentos   || 0, color: BLUE  },
          ].map((s) => (
            <Box
              key={s.label}
              sx={{
                flex: 1, textAlign: 'center',
                bgcolor: '#F8FAFC', borderRadius: 2, py: 1,
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                {s.val}
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#94A3B8', mt: 0.3 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* saldo */}
        <Box
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            pt: 1.2, borderTop: '1px dashed #E2E8F0',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>Saldo</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: saldo >= 0 ? GREEN : RED }}>
            {fmt(saldo)}
          </Typography>
        </Box>
      </CardContent>

      {/* action */}
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
          onClick={() => onVerPerfil(usuario)}
          sx={{
            borderRadius: 2,
            background: `linear-gradient(135deg, ${BLUE} 0%, #60A5FA 100%)`,
            boxShadow: 'none',
            fontSize: 12.5,
            fontWeight: 600,
            py: 1,
            textTransform: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #1D4ED8 0%, ${BLUE} 100%)`,
              boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
            },
          }}
        >
          Ver Perfil Completo
        </Button>
      </Box>
    </Card>
  );
};

/* ═══════════════════════════════════════
   Componente Principal
═══════════════════════════════════════ */
const Operacional = () => {
  const navigate = useNavigate();
  const [modo,         setModo]         = useState('clinicas');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [usuarios,     setUsuarios]     = useState([]);
  const [clinicas,     setClinicas]     = useState([]);
  const [page,         setPage]         = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total,        setTotal]        = useState(0);
  const [search,       setSearch]       = useState('');
  const [searchInput,  setSearchInput]  = useState('');

  const carregarClinicas = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const d = await clinicaService.listarClinicasOperacional({ page, limit: 12, search });
      setClinicas(d.clinicas);
      setTotalPaginas(d.totalPaginas);
      setTotal(d.total || d.clinicas.length);
    } catch { setError('Erro ao carregar clínicas'); }
    finally  { setLoading(false); }
  }, [page, search]);

  const carregarUsuarios = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const d = await operacionalService.listarUsuariosOperacional({ page, limit: 12, search });
      setUsuarios(d.usuarios);
      setTotalPaginas(d.totalPaginas);
      setTotal(d.totalUsuarios || d.usuarios.length);
    } catch { setError('Erro ao carregar usuários'); }
    finally  { setLoading(false); }
  }, [page, search]);

  useEffect(() => {
    if (modo === 'clinicas') carregarClinicas();
    else                     carregarUsuarios();
  }, [modo, page, search]); // eslint-disable-line

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleModo   = (m)  => { setModo(m); setPage(1); setSearch(''); setSearchInput(''); };

  const isClinica = modo === 'clinicas';
  const lista     = isClinica ? clinicas : usuarios;

  return (
    <Box sx={{ pb: 4 }}>

      {/* ── Hero header ── */}
      <Box
        sx={{
          mb: 3, p: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', right: -30, top: -30, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.07)' }} />
        <Box sx={{ position: 'absolute', right: 100, bottom: -50, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.06)' }} />
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 0.4 }}>
            Painel Operacional
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)' }}>
            Gerencie clínicas e usuários, acompanhe lançamentos e envie documentos
          </Typography>

          {/* quick stats */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
            {[
              { icon: BusinessIcon, label: isClinica ? 'Clínicas' : 'Usuários', val: total },
              { icon: ReceiptIcon,  label: 'Página atual',                       val: `${page} / ${totalPaginas || 1}` },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <Box
                  key={s.label}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 0.9,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 2,
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: ORANGE_L }} />
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'rgba(147,197,253,0.7)', lineHeight: 1 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{s.val}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Barra de filtros ── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          mb: 3, flexWrap: 'wrap',
        }}
      >
        {/* Toggle pills */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            p: 0.5,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {[
            { key: 'clinicas',  label: 'Clínicas',  Icon: BusinessIcon },
            { key: 'usuarios',  label: 'Usuários',  Icon: PersonIcon   },
          ].map(({ key, label, Icon }) => {
            const active = modo === key;
            return (
              <Box
                key={key}
                onClick={() => handleModo(key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8,
                  px: 1.8, py: 0.8,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  background: active
                    ? `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`
                    : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(249,115,22,0.3)' : 'none',
                }}
              >
                <Icon sx={{ fontSize: 16, color: active ? '#fff' : '#94A3B8' }} />
                <Typography
                  sx={{
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? '#fff' : '#64748B',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder={isClinica ? 'Buscar clínicas por nome ou CNPJ...' : 'Buscar usuários por nome ou e-mail...'}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, fontSize: 13, bgcolor: '#fff' },
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{
            borderRadius: 2,
            background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
            boxShadow: 'none',
            fontWeight: 600,
            fontSize: 13,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            flexShrink: 0,
            '&:hover': { boxShadow: '0 4px 12px rgba(249,115,22,0.35)' },
          }}
        >
          Buscar
        </Button>
      </Box>

      {/* ── Conteúdo ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: ORANGE }} size={40} thickness={3} />
            <Typography sx={{ mt: 2, color: '#94A3B8', fontSize: 13 }}>Carregando...</Typography>
          </Box>
        </Box>
      ) : lista.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center', py: 10,
            bgcolor: '#fff', borderRadius: 3,
            border: '1px solid #E2E8F0',
          }}
        >
          {isClinica
            ? <BusinessIcon sx={{ fontSize: 56, color: '#E2E8F0', mb: 2 }} />
            : <PersonIcon   sx={{ fontSize: 56, color: '#E2E8F0', mb: 2 }} />}
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#CBD5E1' }}>
            {search ? 'Nenhum resultado encontrado' : `Nenhum${isClinica ? 'a clínica' : ' usuário'} cadastrado`}
          </Typography>
          {search && (
            <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.5 }}>
              Tente um termo diferente
            </Typography>
          )}
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {isClinica
            ? clinicas.map((c) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
                  <ClinicaCard clinica={c} onVerPerfil={(cl) => navigate(`/admin/operacional/clinica/${cl.id}`)} />
                </Grid>
              ))
            : usuarios.map((u) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={u.id}>
                  <UserCard usuario={u} onVerPerfil={(us) => navigate(`/admin/operacional/usuario/${us.id}`)} />
                </Grid>
              ))}
        </Grid>
      )}

      {/* ── Paginação ── */}
      {!loading && totalPaginas > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPaginas}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{
              '& .MuiPaginationItem-root': { fontSize: 12 },
              '& .Mui-selected': { bgcolor: `${ORANGE} !important`, color: '#fff' },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Operacional;
